import { BetSettlementStatus, BetStatus, BetType } from '../../sportsbook-types';

export type AuthenticationRefreshResponse = {
	bettingSessionId: string;
	bettingSessionExpiresOn: string;
};

// Bets

export type BetBuilderResponseItem = {
	eventId: string;
	marketId: string;
	selectionId: string;
	betBuilderSelectionId: string;
	selectionIdentifier: string;
	betSlipUid: string;
	externalDataProviderId: string;
};

export type BetItem = {
	selectionId: string;
	selectionDisplayName: string;
	marketId: string;
	marketDisplayName: string;
	eventDisplayName: string;
	finalOdds: number;
	initOdds?: number;
	status: BetStatus;
	settlementStatus: BetSettlementStatus;
	startEventDate: Date;
	sportId: number;
	leagueName?: string;
	eventId: string;
	isPreCanned: boolean;
	isBetBuilder: boolean;
	homeTeamName: string;
	awayTeamName: string;
};

export enum BetBonusType {
	FREE_BET = 'FREE_BET_TOKEN',
	RISK_FREE_BET = 'RISK_FREE_BET_TOKEN',
	BET_BOOST = 'BET_BOOST_TOKEN',
	SUPER_BOOST = 'SUPER_BOOST_TOKEN'
}

export type BetBonus = {
	givenBonusTokenId: number;
	name: string;
	bonusType: BetBonusType;
	freeBetTokenAmount: number; // Used with BetBonusType.FREE_BET
	boostPercentage: number; // Used with BetBonusType.BET_BOOST
	boostOdds: number; // Used with BetBonusType.SUPER_BOOST
	maxStake: number;
	minStake: number;
};

export type Bonus = {
	type?: BetBonusType;
	freeBetTokenAmount?: number;
	boostPercentage?: number;
	boostOdds: number;
};

export type Bet = {
	id: number;
	type: BetType;
	systemBetType?: string;
	status: BetStatus;
	settlementStatus: BetSettlementStatus;
	placementDate: string;
	settlementDate: string;
	stake: number;
	finalStake: number;
	finalOdds: number;
	initOdds: number;
	bonus?: Bonus;
	items: BetItem[];
	stakeTax: number;
	stakeTaxPercentage: number;
	createdDate: string;
	returns: number;
	finalReturns: number;
	returnsTax: number;
	returnsTaxPercentage: number;
};

export type BetsResponse = {
	items: Bet[];
	startDate?: string;
	endDate?: string;
	totalSize: number;
};

// Betting

export type CheckBetsRequest = {
	bets: Array<CheckBetsRequestBet>;
};

export type CheckBetsRequestBet = {
	ref: string;
	type: BetType;
	stake: number;
	items: Array<RequestItem>;
	combination: number;

	givenBonusTokenId?: number;
	isFreeBet?: boolean;
};

export type RequestItem = {
	eventId: string;
	marketId: string;
	selectionId: string;
	marketTypeId: string;
	sportId: string;
	leagueId: string;
	odds: number;
	points?: number;
	isLive: boolean;
};

export type CheckBetsResponse = {
	bets: Array<CheckBetsResponseBet>;
};

export type CheckBetsResponseBet = {
	ref: string;
	type: BetType;

	minStake: number;
	maxStake: number;

	combination: number;
	betCount: number;

	originalOdds?: number;
	odds: number;
	finalOdds: number;
	stake: number;
	finalStake: number;
	stakeTax?: number;
	stakeTaxPercentage?: number;

	returns: number;
	finalReturns: number;
	returnsTax?: number;
	returnsTaxPercentage?: number;

	bonusTokens: Array<BetBonus>;

	errors: Array<CheckBetsResponseError>;
	warnings: Array<CheckBetsResponseWarning>;
};

export enum CheckBetsErrorType {
	UNAVAILABLE = 'UNAVAILABLE',
	ITEM_UNAVAILABLE = 'ITEM_UNAVAILABLE',
	STAKE_TOO_LOW = 'STAKE_TOO_LOW',
	STAKE_TOO_HIGH = 'STAKE_TOO_HIGH',
	COMBO_NOT_ALLOWED = 'COMBO_NOT_ALLOWED',
	NON_COMBINABLE_SELECTIONS = 'NON_COMBINABLE_SELECTIONS',
	INVALID_BONUS = 'INVALID_BONUS',
	INVALID_BONUS_STAKE_TOO_LOW = 'INVALID_BONUS_STAKE_TOO_LOW',
	INVALID_BONUS_STAKE_TOO_HIGH = 'INVALID_BONUS_STAKE_TOO_HIGH',
	ITEM_COUNT_TOO_LOW = 'ITEM_COUNT_TOO_LOW',
	ITEM_COUNT_TOO_HIGH = 'ITEM_COUNT_TOO_HIGH',
	ODDS_TOO_LOW = 'ODDS_TOO_LOW',
	ODDS_TOO_HIGH = 'ODDS_TOO_HIGH',
	PLAYER_BALANCE_TOO_LOW = 'PLAYER_BALANCE_TOO_LOW',
	PLAYER_STAKE_LIMIT_REACHED = 'PLAYER_STAKE_LIMIT_REACHED',
	PLAYER_LOSS_LIMIT_REACHED = 'PLAYER_LOSS_LIMIT_REACHED',
	ODDS_UPDATED = 'ODDS_UPDATED',
	STAKE_HIGHER_THAN_WIN = 'STAKE_HIGHER_THAN_WIN',
	OTHER = 'OTHER'
}

export enum CheckBetsWarningType {
	MAXIMUM_RETURNS_EXCEEDED = 'MAXIMUM_RETURNS_EXCEEDED'
}

export type CheckBetsResponseError = {
	betRef: string;
	type: CheckBetsErrorType;
	description: string;
	selectionIds?: Array<string>;
	minStake?: number;
	maxStake?: number;
};

export type CheckBetsResponseWarning = {
	betRef: string;
	type: CheckBetsWarningType;
	description: string;
	selectionIds?: Array<string>;
};

type PlaceBetsRequestBet = {
	ref: string;
	type: BetType;
	items: Array<RequestItem>;
	combination: number;

	odds: number;
	finalOdds: number;
	oddsBonusPercentage: number;

	givenBonusTokenId?: number;
	isFreeBet?: boolean;

	stake: number;
	stakeTax?: number;
	stakeTaxPercentage?: number;

	returns: number;
	finalReturns: number;
	returnsTax?: number;
	returnsTaxPercentage?: number;
};

export type PlaceBetsResponseBetItem = {
	selectionId: string;
	odds: number;
};

export enum PlaceBetsResponseBetStatus {
	DELAYED = 'Delayed',
	PLACED = 'Placed',
	ERROR = 'Error'
}

export type PlaceBetsResponseBet = {
	ref: string;
	status: PlaceBetsResponseBetStatus;

	odds: number;
	finalOdds: number;

	items: Array<PlaceBetsResponseBetItem>;
	newOffer?: NewOffer;
	errors: Array<PlaceBetsResponseError>;

	stake: number;
	finalStake: number;
	stakeTax?: number;
	stakeTaxPercentage?: number;
	createdDate?: string;
	returns: number;
	finalReturns: number;
	returnsTax?: number;
	returnsTaxPercentage?: number;
};

export type PlaceBetsRequest = {
	bets: Array<PlaceBetsRequestBet>;
	acceptAllOddChanges: boolean;
};

export enum PlaceBetsErrorType {
	UNAVAILABLE = 'UNAVAILABLE',
	ITEM_UNAVAILABLE = 'ITEM_UNAVAILABLE',
	STAKE_TOO_LOW = 'STAKE_TOO_LOW',
	STAKE_TOO_HIGH = 'STAKE_TOO_HIGH',
	COMBO_NOT_ALLOWED = 'COMBO_NOT_ALLOWED',
	NON_COMBINABLE_SELECTIONS = 'NON_COMBINABLE_SELECTIONS',
	INVALID_BONUS = 'INVALID_BONUS',
	INVALID_BONUS_STAKE_TOO_LOW = 'INVALID_BONUS_STAKE_TOO_LOW',
	INVALID_BONUS_STAKE_TOO_HIGH = 'INVALID_BONUS_STAKE_TOO_HIGH',
	ITEM_COUNT_TOO_LOW = 'ITEM_COUNT_TOO_LOW',
	ITEM_COUNT_TOO_HIGH = 'ITEM_COUNT_TOO_HIGH',
	ODDS_TOO_LOW = 'ODDS_TOO_LOW',
	ODDS_TOO_HIGH = 'ODDS_TOO_HIGH',
	PLAYER_BALANCE_TOO_LOW = 'PLAYER_BALANCE_TOO_LOW',
	PLAYER_STAKE_LIMIT_REACHED = 'PLAYER_STAKE_LIMIT_REACHED',
	PLAYER_LOSS_LIMIT_REACHED = 'PLAYER_LOSS_LIMIT_REACHED',
	ODDS_UPDATED = 'ODDS_UPDATED',
	STAKE_HIGHER_THAN_WIN = 'STAKE_HIGHER_THAN_WIN',
	OTHER = 'OTHER',

	PLAYER_AUTH_FAILED = 'PLAYER_AUTH_FAILED',
	PLAYER_BET_LIMIT_PER_DAY_EXCEEDED = 'PLAYER_BET_LIMIT_PER_DAY_EXCEEDED',
	PLAYER_BET_LIMIT_PER_WEEK_EXCEEDED = 'PLAYER_BET_LIMIT_PER_WEEK_EXCEEDED',
	PLAYER_BET_LIMIT_PER_MONTH_EXCEEDED = 'PLAYER_BET_LIMIT_PER_MONTH_EXCEEDED',
	PLAYER_TIME_LIMIT_MISSING = 'PLAYER_TIME_LIMIT_MISSING',
	PLAYER_TIME_LIMIT_PER_DAY_EXCEEDED = 'PLAYER_TIME_LIMIT_PER_DAY_EXCEEDED',
	PLAYER_TIME_LIMIT_PER_WEEK_EXCEEDED = 'PLAYER_TIME_LIMIT_PER_WEEK_EXCEEDED',
	PLAYER_TIME_LIMIT_PER_MONTH_EXCEEDED = 'PLAYER_TIME_LIMIT_PER_MONTH_EXCEEDED',
	PLAYER_IN_COOL_OFF_TIME = 'PLAYER_IN_COOL_OFF_TIME',
	PLAYER_AGE_NOT_VERIFIED = 'PLAYER_AGE_NOT_VERIFIED',
	LUGAS_ACTIVE_ON_OTHER_OPERATOR = 'LUGAS_ACTIVE_ON_OTHER_OPERATOR',
	LUGAS_ON_SITE_COOLOFF = 'LUGAS_ON_SITE_COOLOFF',
	LUGAS_ACTIVE_ON_CURRENT_OPERATOR = 'LUGAS_ACTIVE_ON_CURRENT_OPERATOR',
	PLAYER_STATUS_GAMEPLAY_BLOCKED = 'PLAYER_STATUS_GAMEPLAY_BLOCKED',
	SPORTSBOOK_PRODUCT_BLOCKED = 'SPORTSBOOK_PRODUCT_BLOCKED',
	BALANCE_LIMIT_EXCEEDED = 'BALANCE_LIMIT_EXCEEDED',
	REALITY_CHECK_LIMIT_EXCEEDED = 'REALITY_CHECK_LIMIT_EXCEEDED'
}

export type PlaceBetsResponseError = {
	betRef: string;
	type: PlaceBetsErrorType;
	description: string;
	selectionIds: Array<string>;
	minStake?: number;
	maxStake?: number;
};

export type NewOfferItem = {
	selectionId: string;
	odds: number;
};

export type NewOffer = {
	betRef: string;
	odds: number;
	finalOdds: number;

	returns: number;
	finalReturns: number;
	returnsTax: number;
	returnsTaxPercentage: number;

	items: Array<NewOfferItem>;
};

export type PlaceBetsResponse = {
	bets: Array<PlaceBetsResponseBet>;
};

export enum AcceptMode {
	ALWAYS_ASK = 'ALWAYS_ASK',
	ACCEPT_HIGHER = 'ACCEPT_HIGHER',
	ACCEPT_ALL_CHANGES = 'ACCEPT_ALL_CHANGES'
}

export type UpdateAcceptModeRequest = {
	acceptMode: AcceptMode;
};

export type AcceptModeResponse = {
	acceptMode: AcceptMode;
};

// Cashout

export type CalculateCashoutResponse = {
	betId: number;
	amount: number;
};

export type SubmitCashoutRequest = {
	ref: string;
	betId: number;
	amount: number;
};

export enum SubmitCashoutStatus {
	DELAYED = 'Delayed',
	SUCCESS = 'Success',
	ERROR = 'Error',
	NEW_OFFER = 'NewOffer'
}

export type SubmitCashoutResponse = {
	ref: string;
	betId: number;
	guid: string;
	amount: number;
	errorDescription: string;
	status: SubmitCashoutStatus;
	newOfferAmount: number;
};

// TODO refactor names of all of our different bonus types
export type SportsbookBonus = {
	name: string;
	givenBonusTokenId: number;
	bonusType: BetBonusType; // Ignoring the other 3 bonus types
	expiresOn?: string;
	minOdds?: number;
	maxOdds?: number;
	minCombinations?: number;
	maxCombinations?: number;
	minStake?: number;
	maxStake?: number;
	boostPercentage?: number;
	freeBetAmount?: number;

	// These props are not used and should prob be refactored on the backend (should be our ids)
	sportId: number;
	leagueId: number;
	eventId: number;
	marketId: number;
	boostedOdds?: number;
	gameModeTypes: Array<string>; // not sure about this one
};

export type ListActiveBonusesResponse = Array<SportsbookBonus>;
